import { z } from 'zod/v4';

import {
    advancedBorderSettingsSchema,
    advancedRadiusSchema,
    buttonHorizontalPositionSchema,
    buttonSizeSchema,
    buttonStyleTypeSchema,
    extendedPositionPluginShapeLegacy,
    shadowPluginShapeLegacy,
    socialIconTypeSchema,
    socialNetworkSchema,
    themeButtonDataSchemaLegacy,
    themeButtonHoverSchemaLegacy,
    visibilityPluginShapeLegacy,
    widgetPositionPluginShapeLegacy,
} from '../../../common/schemas/legacy';

import { socialAppearanceSchema, socialItemSchema } from './item';

/**
 * Data shape for the **default-placement** social variant — the legacy "drop
 * into a normal column" form. This is the canonical social data schema; the
 * zero variant below derives from it via `.extend()`.
 *
 * The widget renders one shared style for every entry, so the appearance
 * settings (`type`/`iconType`/`styleType`/theme-button bags) live at the data
 * level and are mirrored onto each item by the editor. `socialNetwork` is the
 * top-level twin of the per-item field, kept for parity with the persisted
 * shape. `height` is the icon size in px (only meaningful for the icon
 * appearance).
 *
 * Leniency mirrors the button widget's data schema: `nullish()` on the
 * per-device aligns, `buttonSize`, the advanced radius/border bags and the
 * shadow pair (all post-date the widget), and `isSynchronizeStyle` nullable —
 * very old widgets persisted `null` and the editor treats that as `true`.
 */
export const socialLegacyDefaultDataSchema = z.object({
    socials: z.array(socialItemSchema),
    socialNetwork: socialNetworkSchema,
    type: socialAppearanceSchema,
    iconType: socialIconTypeSchema,
    styleType: buttonStyleTypeSchema,
    horizontalAlign: buttonHorizontalPositionSchema,
    mobileHorizontalAlign: buttonHorizontalPositionSchema.nullish(),
    tabletHorizontalAlign: buttonHorizontalPositionSchema.nullish(),
    height: z.number(),
    buttonSize: buttonSizeSchema.nullish(),
    buttonBase: themeButtonDataSchemaLegacy,
    buttonHover: themeButtonHoverSchemaLegacy.nullish(),
    isIndependentCorners: z.boolean().nullish(),
    isSynchronizeStyle: z.boolean().nullable(),
    advancedRadius: advancedRadiusSchema.nullish(),
    advancedBorderSettings: advancedBorderSettingsSchema.nullish(),
    ...visibilityPluginShapeLegacy,
    ...widgetPositionPluginShapeLegacy,
    ...shadowPluginShapeLegacy,
});

/**
 * Data shape for the **zero-block-placement** social variant — the default
 * schema with `position` overridden (zod v4 `.extend()` overwrites existing
 * keys) to the full extended-position grid. Zero blocks post-date every other
 * social feature, so the extended position is required — a zero-tagged social
 * widget without it is a validation error by design.
 *
 * Mirrors the FormGroup produced by `getZeroSocialWidgetData` in the host app.
 */
export const socialLegacyZeroDataSchema = socialLegacyDefaultDataSchema.extend({
    ...extendedPositionPluginShapeLegacy,
});
